/**
 * Rep Performance Section
 *
 * Per-rep (Zendesk assignee) support metrics: ticket volume, first reply
 * times (median/p90), and solve times. Self-fetches based on the current
 * clinic and date range.
 */

import { format } from 'date-fns'
import { useState, useEffect, useMemo } from 'react'
import { zendeskAPI } from '../services/api'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from './ui'
import { Loader2, AlertCircle, Users } from 'lucide-react'

// Format time in minutes to human readable
function formatMinutes(mins) {
  if (mins == null) return '--'
  if (mins < 60) return `${Math.round(mins)}m`
  if (mins < 1440) return `${(mins / 60).toFixed(1)}h`
  return `${(mins / 1440).toFixed(1)}d`
}

// Format hours to human readable
function formatHours(hrs) {
  if (hrs == null) return '--'
  if (hrs < 1) return `${Math.round(hrs * 60)}m`
  if (hrs < 24) return `${hrs.toFixed(1)}h`
  return `${(hrs / 24).toFixed(1)}d`
}

// P90 reply badge color - mirrors thresholds used elsewhere (minutes)
function getP90ReplyStyle(mins) {
  if (mins == null) return { backgroundColor: '#6B7280', color: '#FFFFFF' }
  if (mins <= 60) return { backgroundColor: '#22C55E', color: '#FFFFFF' }     // <= 1h green
  if (mins <= 240) return { backgroundColor: '#84CC16', color: '#FFFFFF' }    // <= 4h lime
  if (mins <= 720) return { backgroundColor: '#EAB308', color: '#000000' }    // <= 12h yellow
  if (mins <= 1440) return { backgroundColor: '#F97316', color: '#FFFFFF' }   // <= 24h orange
  return { backgroundColor: '#EF4444', color: '#FFFFFF' }                     // > 24h red
}

function displayName(rep) {
  return rep.name || `Agent …${rep.assignee_id.slice(-4)}`
}

const COLUMNS = [
  { field: 'tickets', label: 'Tickets' },
  { field: 'solved', label: 'Solved' },
  { field: 'median_first_reply_minutes', label: 'Median First Reply' },
  { field: 'p90_first_reply_minutes', label: 'P90 First Reply' },
  { field: 'median_solve_hours', label: 'Median Solve' },
  { field: 'p90_solve_hours', label: 'P90 Solve' },
]

export default function RepPerformance({ dateRange }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortField, setSortField] = useState('tickets')
  const [sortDirection, setSortDirection] = useState('desc')

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) return

    let cancelled = false
    const fetchReps = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await zendeskAPI.getReps({
          startDate: format(dateRange.from, 'yyyy-MM-dd'),
          endDate: format(dateRange.to, 'yyyy-MM-dd'),
        })
        if (cancelled) return
        setData(response.data)
      } catch (err) {
        if (cancelled) return
        console.error('[REP PERFORMANCE] Error:', err)
        setError(err.response?.data?.error || 'Failed to load rep metrics')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchReps()
    return () => { cancelled = true }
  }, [dateRange])

  const sortedReps = useMemo(() => {
    const reps = [...(data?.reps || [])]
    reps.sort((a, b) => {
      const aVal = a[sortField] ?? -Infinity
      const bVal = b[sortField] ?? -Infinity
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
    return reps
  }, [data, sortField, sortDirection])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  if (data?.brand_not_supported) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Rep Performance
        </CardTitle>
        <CardDescription>
          Per-rep response and resolution times for tickets created in this period.
          First reply is attributed to the ticket&apos;s assignee.
          {data && !data.names_available && (
            <span className="block mt-1 text-amber-600 dark:text-amber-500">
              Zendesk API credentials not configured — showing agent IDs instead of names
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading rep metrics...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-destructive">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        ) : sortedReps.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No assigned tickets in this period
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rep
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.field}
                      className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted whitespace-nowrap"
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                      {sortField === col.field && (
                        <span className="ml-1">{sortDirection === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {sortedReps.map((rep) => (
                  <tr key={rep.assignee_id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                      {displayName(rep)}
                      {rep.replied < rep.tickets && (
                        <span
                          className="ml-2 text-xs text-muted-foreground"
                          title="Tickets with a first reply recorded"
                        >
                          ({rep.replied} replied)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-foreground font-medium">
                      {rep.tickets.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {rep.solved.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {formatMinutes(rep.median_first_reply_minutes)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className="px-2 py-0.5 text-sm font-medium rounded"
                        style={getP90ReplyStyle(rep.p90_first_reply_minutes)}
                      >
                        {formatMinutes(rep.p90_first_reply_minutes)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {formatHours(rep.median_solve_hours)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {formatHours(rep.p90_solve_hours)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
